import { type ReactNode, useEffect, useRef } from 'react';
import { Controller, FormProvider, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AddOnsPresetsModal } from '@components/add-ons-presets-modal';
import { AddOnsSection } from '@components/add-ons-section';
import { AdmissionStatusSelect } from '@components/admission-status-select';
import { AdvancedSettingsSection } from '@components/advanced-settings-section';
import { AppearanceSection } from '@components/appearance-section';
import { BasicInfoSection } from '@components/basic-info-section';
import { DistributionSection } from '@components/distribution-section';
import { DrawerFormFooter, DrawerFormHeader, DrawerFormLayout } from '@components/drawer-form-layout';
import { FormFieldsSection } from '@components/form-fields-section';
import { ModalV2 } from '@components/modals';
import { SelectTriggerPassModal } from '@components/passes/pass-create-modal/select-trigger-pass-modal/select-trigger-pass-modal';
import { RegulationSettingsSection } from '@components/regulation-settings-section';
import { VatRateSection } from '@components/vat-rate-section';
import { ADMISSION_DRAWER_OVERLAY_Z_INDEX, ADMISSION_DRAWER_SURFACE_CLASSNAME } from '@constants/admission-drawer';

import {
  type AdmissionFormProps,
  type PassFormProps,
  type PresetFormProps,
  type SectionKey,
} from './admission-form.type';
import {
  ADMISSION_FORM_TEST_ID,
  SECTION_ADD_ONS,
  SECTION_ADVANCED,
  SECTION_APPEARANCE,
  SECTION_BASIC_INFO,
  SECTION_DISTRIBUTION,
  SECTION_FORM_FIELDS,
  SECTION_REGULATION,
  SECTION_VAT_RATE,
} from './constants';
import { isFreeOfferPrice } from './is-free-offer-price';
import { useAdmissionForm, type UseAdmissionFormResult } from './use-admission-form';
import { usePassForm } from './use-pass-form';
import { usePresetForm } from './use-preset-form';

export { ADMISSION_FORM_TEST_ID };

/**
 * Scrolls a freshly expanded section's header to the top of the drawer's scroll area. Fires only on
 * the closed→open transition, so the initial mount and re-renders from typing never yank the scroll.
 */
const SectionScrollAnchor = ({ open, children }: { open: boolean; children: ReactNode }): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(open);

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (justOpened) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [open]);

  return <div ref={ref}>{children}</div>;
};

/**
 * Presentational shell shared by the admission and pass flows. It renders purely from a
 * `UseAdmissionFormResult`, so either orchestration hook (`useAdmissionForm` / `usePassForm`) can
 * drive it.
 */
export const AdmissionFormView = ({
  form,
  isSaving = false,
}: {
  form: UseAdmissionFormResult;
  isSaving?: boolean;
}): JSX.Element => {
  const { t } = useTranslation('offers');
  const { control, handleSubmit } = form.methods;
  const submitLockRef = useRef(false);
  useEffect(() => {
    if (!isSaving) submitLockRef.current = false;
  }, [isSaving]);
  const onGuardedSave = () => {
    if (submitLockRef.current || isSaving) return;
    if (form.onSave()) submitLockRef.current = true;
  };

  const watchedPrice = useWatch({ control, name: 'basicInfo.price' });
  const isFreeOffer = isFreeOfferPrice(watchedPrice);

  const renderSection = (key: SectionKey): JSX.Element | null => {
    const open = form.openSection === key;
    const onToggleOpen = () => form.toggleSection(key);

    switch (key) {
      case SECTION_BASIC_INFO:
        return (
          <Controller
            key={key}
            name='basicInfo'
            control={control}
            render={({ field }) => (
              <BasicInfoSection
                value={field.value}
                onChange={(patch) => field.onChange({ ...field.value, ...patch })}
                open={open}
                onToggleOpen={onToggleOpen}
                showTopBorder={false}
                currency={form.currency}
                mainLanguage={form.mainLanguage}
                salesWindowContext={form.salesWindowContext}
                disabled={form.isEventInThePast}
                lockPricing={form.isPricingLocked}
                lockDownpayment={form.isDownpaymentLocked}
                lockFields={form.isEditingLocked}
                errors={form.errors.basicInfo}
                onOpenStartAfter={form.onOpenStartAfter}
                maxItemsPlaceholder={form.maxItemsPlaceholder}
              />
            )}
          />
        );
      case SECTION_DISTRIBUTION:
        return (
          <Controller
            key={key}
            name='distribution'
            control={control}
            render={({ field }) => (
              <DistributionSection
                value={field.value}
                onChange={(patch) => field.onChange({ ...field.value, ...patch })}
                open={open}
                onToggleOpen={onToggleOpen}
                disabled={form.isEventInThePast || form.isEditingLocked}
                channelErrors={form.channelErrors}
                channelLimitError={form.channelLimitError}
                channelLimitLoading={form.channelLimitLoading}
                accessCodeError={form.accessCodeError}
              />
            )}
          />
        );
      case SECTION_ADD_ONS:
        if (form.isAddOnsSectionHidden) return null;
        return (
          <Controller
            key={key}
            name='addOns'
            control={control}
            render={({ field }) => (
              <AddOnsSection
                value={field.value}
                onChange={(patch) => field.onChange({ ...field.value, ...patch })}
                open={open}
                onToggleOpen={onToggleOpen}
                disabled={form.isEventInThePast || form.isEditingLocked}
                availableAddOns={form.availableAddOns}
                isLoading={form.addOnsLoading}
                onOpenPresetsModal={form.onOpenPresetsModal}
                hiddenReason={form.addOnsHiddenReason}
                errors={form.errors.addOns}
              />
            )}
          />
        );
      case SECTION_ADVANCED:
        return (
          <Controller
            key={key}
            name='advanced'
            control={control}
            render={({ field }) => (
              <AdvancedSettingsSection
                value={field.value}
                onChange={(patch) => field.onChange({ ...field.value, ...patch })}
                open={open}
                onToggleOpen={onToggleOpen}
                timezone={form.timezone}
                locale={form.mainLanguage}
                minValidFromDate={form.minValidFromDate}
                minValidUntilDate={field.value.entranceTimeSlot.validFrom ?? form.minValidFromDate}
                maxEndDate={form.maxEndDate}
                isEventInThePast={form.isEventInThePast}
                lockLowStockThreshold={form.isEditingLocked}
                errors={form.errors.advanced}
              />
            )}
          />
        );
      case SECTION_FORM_FIELDS:
        return (
          <Controller
            key={key}
            name='formFields'
            control={control}
            render={({ field }) => (
              <FormFieldsSection
                value={field.value}
                onChange={(patch) => field.onChange({ ...field.value, ...patch })}
                open={open}
                onToggleOpen={onToggleOpen}
                disabled={form.isEventInThePast || form.isFormFieldsLocked || form.isEditingLocked}
                presets={form.formFieldPresets}
                isPhoneLockedRequired={form.isPhoneLockedRequired}
              />
            )}
          />
        );
      case SECTION_APPEARANCE:
        return (
          <Controller
            key={key}
            name='appearance'
            control={control}
            render={({ field }) => (
              <AppearanceSection
                value={field.value}
                onChange={(patch) => field.onChange({ ...field.value, ...patch })}
                open={open}
                onToggleOpen={onToggleOpen}
                disabled={form.isEventInThePast || form.isEditingLocked}
                galleryController={form.galleryController}
                floorplanImageUrl={form.floorplanImageUrl}
              />
            )}
          />
        );
      case SECTION_VAT_RATE:
        return (
          <Controller
            key={key}
            name='vatRate'
            control={control}
            render={({ field }) => (
              <VatRateSection
                value={field.value}
                onChange={(patch) => field.onChange({ ...field.value, ...patch })}
                open={open}
                onToggleOpen={onToggleOpen}
                disabled={form.isEventInThePast || form.isEditingLocked}
                errors={form.errors.vatRate}
              />
            )}
          />
        );
      case SECTION_REGULATION:
        return (
          <Controller
            key={key}
            name='regulation'
            control={control}
            render={({ field }) => (
              <RegulationSettingsSection
                value={field.value}
                onChange={(patch) => field.onChange({ ...field.value, ...patch })}
                open={open}
                onToggleOpen={onToggleOpen}
                disabled={form.isEventInThePast || form.isEditingLocked}
                isFreeOffer={isFreeOffer}
                isSiaeApprovalAllowed={form.isSiaeApprovalAllowed}
                errors={form.errors.regulation}
              />
            )}
          />
        );
      default:
        return null;
    }
  };

  return (
    <FormProvider {...form.methods}>
      <DrawerFormLayout
        testId={ADMISSION_FORM_TEST_ID}
        onSubmit={handleSubmit(onGuardedSave)}
        header={
          <DrawerFormHeader
            title={form.title}
            rightSlot={
              form.showStatusControl ? (
                <Controller
                  name='status'
                  control={control}
                  render={({ field }) => (
                    <AdmissionStatusSelect
                      value={field.value}
                      onChange={(newStatus) => form.handleStatusChange(newStatus, field.onChange)}
                      disabled={form.statusLock.blocked || form.isEventInThePast}
                      disabledReason={form.statusLock.blocked ? t(form.statusLock.reasonKey) : undefined}
                    />
                  )}
                />
              ) : undefined
            }
          />
        }
        footer={
          <DrawerFormFooter
            onDiscard={form.onClose}
            discardLabel={t('common:discard')}
            primaryLabel={form.submitLabel}
            primaryDisabled={form.isSaveDisabled || isSaving}
          />
        }>
        {form.visibleSections.map((key) => {
          const section = renderSection(key);
          return section ? (
            <SectionScrollAnchor key={key} open={form.openSection === key}>
              {section}
            </SectionScrollAnchor>
          ) : null;
        })}
      </DrawerFormLayout>

      <AddOnsPresetsModal
        isOpen={form.isAddOnsPresetsModalOpen}
        onClose={form.closeAddOnsPresetsModal}
        eventId={form.eventId}
        companyClubId={form.companyClubId}
        onSaved={form.onAddOnsPresetsSaved}
        surfaceClassName={ADMISSION_DRAWER_SURFACE_CLASSNAME}
        zIndex={ADMISSION_DRAWER_OVERLAY_Z_INDEX}
      />
    </FormProvider>
  );
};

export const AdmissionForm = (props: AdmissionFormProps): JSX.Element => (
  <AdmissionFormView form={useAdmissionForm(props)} isSaving={props.isSaving} />
);

export const PresetForm = (props: PresetFormProps): JSX.Element => (
  <AdmissionFormView form={usePresetForm(props)} isSaving={props.isSaving} />
);

export const PassForm = (props: PassFormProps): JSX.Element => {
  const form = usePassForm(props);
  const { startAfterSelector } = form;
  return (
    <>
      <AdmissionFormView form={form} isSaving={props.isSaving} />
      <ModalV2
        isOpen={startAfterSelector.isOpen}
        onClose={startAfterSelector.onClose}
        maxWidthClass='lg:max-w-(--breakpoint-md)'
        surfaceClassName={ADMISSION_DRAWER_SURFACE_CLASSNAME}
        zIndex={ADMISSION_DRAWER_OVERLAY_Z_INDEX}>
        <SelectTriggerPassModal
          passes={startAfterSelector.passes}
          passId={startAfterSelector.selectedPassId}
          onSelect={startAfterSelector.onSelect}
          handleClose={startAfterSelector.onClose}
        />
      </ModalV2>
    </>
  );
};
